// src/redux/slices/masterCertificatesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

// ===== helpers =====

// срезать Content-Type, чтобы multipart не ломался
const stripContentType = (h = {}) => {
  const copy = { ...h };
  delete copy['Content-Type'];
  delete copy['content-type'];
  if (copy.common) {
    delete copy.common['Content-Type'];
    delete copy.common['content-type'];
  }
  if (copy.post) {
    delete copy.post['Content-Type'];
    delete copy.post['content-type'];
  }
  return copy;
};

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true, // сами разрулим status
  };
};

const authMultipartConfig = async () => {
  const headers = stripContentType(await getAuthHeaders());
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

// ===== thunks =====

// получить список сертификатов (приватный)
export const fetchCertificates = createAsyncThunk(
  'masterCertificates/fetchCertificates',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');

      const cfg = await authJsonConfig();
      const response = await axios.get(
        `${getServerUrl()}/master-certificates/${uid}/certificates`,
        cfg
      );

      // Нет сертификатов — не ошибка
      if (response.status === 404) {
        return [];
      }

      // Отказ в доступе — уже реальная ошибка
      if (response.status === 403) {
        throw new Error(
          response.data?.message ||
          'Forbidden (403) while fetching certificates'
        );
      }

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
          `Failed to fetch certificates (HTTP ${response.status})`
        );
      }

      const images = response.data?.files;
      if (!images || !Array.isArray(images) || images.length === 0) {
        return [];
      }

      return images;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка загрузки изображений';
      return rejectWithValue(msg);
    }
  }
);


export const uploadCertificate = createAsyncThunk(
  'masterCertificates/uploadCertificate',
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      if (!file) throw new Error('Файл не передан');

      const formData = new FormData();
      formData.append('uid', uid);
      formData.append('file', file);

      const cfg = await authMultipartConfig();

      const response = await axios.post(
        `${getServerUrl()}/master-certificates`,
        formData,
        cfg
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          response.data?.message ||
          `Failed to upload certificate (HTTP ${response.status})`
        );
      }

      const filePath = response.data?.filePath;
      return filePath || null;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка загрузки сертификата';
      return rejectWithValue(msg);
    }
  }
);

export const deleteCertificate = createAsyncThunk(
  'masterCertificates/deleteCertificate',
  async ({ uid, fileName }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      if (!fileName) throw new Error('fileName не передан');

      const cfg = await authJsonConfig();

      const response = await axios.delete(
        `${getServerUrl()}/master-certificates/${uid}/certificates/${encodeURIComponent(fileName)}`,
        cfg
      );

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
          `Failed to delete certificate (HTTP ${response.status})`
        );
      }

      return fileName;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Ошибка удаления сертификата';
      return rejectWithValue(msg);
    }
  }
);


// публичные сертификаты (без JWT)
export const fetchPublicCertificates = createAsyncThunk(
  'masterCertificates/fetchPublicCertificates',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const base = getServerUrl().replace(/\/$/, '');
      // сервер может вернуть [{...}] или { files: [...] }
      const { data } = await axios.get(`${base}/public/${uid}/certificates`);

      const arr = Array.isArray(data) ? data : (data?.files || []);
      return arr || [];
    } catch (error) {
      const status = error?.response?.status;

      // ✅ Нет сертификатов у мастера (public) — не ошибка
      if (status === 404) {
        return [];
      }

      return rejectWithValue(
        error?.response?.data || error?.message || 'Ошибка загрузки сертификатов (public)'
      );
    }
  }
);

// ===== state =====
const initialState = {
  certificates: [], // список путей/URL сертификатов
  status: 'idle',   // idle | loading | failed
  error: null,
};

// ===== slice =====
const masterCertificatesSlice = createSlice({
  name: 'masterCertificates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchCertificates.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.status = 'idle';
        state.certificates = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCertificates.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // upload
      .addCase(uploadCertificate.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(uploadCertificate.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload) state.certificates.push(action.payload);
      })
      .addCase(uploadCertificate.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // delete
      .addCase(deleteCertificate.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteCertificate.fulfilled, (state, action) => {
        state.status = 'idle';
        const fileName = action.payload;
        state.certificates = state.certificates.filter((url) => !url.includes(fileName));
      })
      .addCase(deleteCertificate.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // публичные сертификаты
      .addCase(fetchPublicCertificates.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPublicCertificates.fulfilled, (state, action) => {
        state.status = 'idle';
        state.certificates = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPublicCertificates.rejected, (state, action) => {
        state.status = 'failed';
        state.error =
          action.payload || action.error?.message || 'Ошибка загрузки сертификатов (public)';
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default masterCertificatesSlice.reducer;
